import _ from 'lodash';
import * as Yup from 'yup';
import { useCallback } from 'react';

import { Button, Input, IRadioProps, Modal, RadioGroup } from '@/shared/ui';
import { ITestClaimTransferPrescriptions, ITransferPrescriptions } from '@/shared/types/interfaces/transfer-prescriptions';
import { useFormik } from 'formik';
import { ERROR_MESSAGES } from '@/shared/constants/error-messages';
import { medicationQuantityInitialValues, medicationDaysSupplyOptions, IMedicationQuantityInitialValues } from '../../../../../../shared/constants/medication-quantity';

import './style.css';

const initialValues = {
    quantity: '30',
    daysSupply: 30,
};

const validationSchema = Yup.object().shape({
    quantity: Yup.number().required(ERROR_MESSAGES.REQUIRED_FIELD_MISSING),
    daysSupply: Yup.number().required(),
});

interface IProps {
    medication: ITestClaimTransferPrescriptions;
    onClose(): void;
    onSubmit(values: IMedicationQuantityInitialValues): void;
}

export const MedicalCardEditQuantity = ({
    medication,
    onClose,
    onSubmit,
}: IProps) => {
    const formik = useFormik({
        initialValues: {
            ...medicationQuantityInitialValues,
            quantity: medication.copayQuantity?.toString() || '30',
            daysSupply: medication.copayDaysSupply || 30,
        },
        validationSchema,
        onSubmit: (values) => {
            onSubmit(values)
        }
    });

    const handleDaysSupplyChange = useCallback(
        (item: IRadioProps) => {
            formik.setFieldValue('daysSupply', item.value);
        },
        [formik]
    );

    return (
        <Modal
            dataTestId="medical-card-edit-quantity"
            defaultPaddings={false}
            open={true}
            onClose={onClose}
            contentClassName="!max-w-[490px] p-5 sm:p-12"
        >
            <form className="flex flex-col items-center justify-center" data-testid="confirm-modal-container" onSubmit={formik.handleSubmit}>
                <h2 className="text-25 md:text-35 text-center mb-5 md:mb-3">{medication.prescribable.name}</h2>
                <p className="text-lg md:text-xl md:text-center mb-5 md:mb-[1.6875rem]">How often do you take this medication, and in what quantity?</p>

                <Input
                    label="Quantity"
                    name="quantity"
                    placeholder="ex. 3"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={!!formik.touched.quantity && !!formik.errors.quantity}
                    errorMessage={formik.errors.quantity}
                    testId="register-form-create-password"
                    className="mb-[0.625rem]"
                    enableNaturalEnter
                />

                <div className="mb-6 edit-quantity-radio-group-wrapper">
                    <RadioGroup
                        controlled
                        optionType="radio"
                        items={medicationDaysSupplyOptions}
                        name="allergies"
                        dataTestId="edit-quantity-options"
                        radioGroupClassName="w-full"
                        buttonClassName="edit-quantity-radio-button"
                        value={formik.values.daysSupply}
                        onOptionClick={handleDaysSupplyChange}
                    />
                </div>

                <div className="flex w-full flex-col">
                    <Button
                        testId="button-confirm-modal-confirm"
                        className={`confirm_modal__confirm`}
                        type="submit"
                        sx={{ width: '100%', marginBottom: '1rem', fontSize: '1.125rem' }}
                        showEndIcon={true}
                        loading={false}
                        disabled={false}
                    >
                        <span className="font-dmSans">Save Changes</span>
                    </Button>
                    <Button
                        testId="button-confirm-modal-cancel"
                        className='confirm_modal__cancel'
                        sx={{ width: '100%', fontSize: '1.125rem' }}
                        variant="outlined"
                        onClick={onClose}
                    >
                        <span>Cancel</span>
                    </Button>
                </div>
            </form>
        </Modal>
    );
};
